import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    ColumnSortPreferences,
    ColumnTemplateDirective,
    findDefaultColumnSelection,
    initColumn,
    resolveValue,
    RowActionsDirective,
    TableColumn,
    TableConfig,
    TableSearchValues
} from '@flyfreely-portal-ui/flyfreely-table';
import {
    CellStyleModule,
    ClientSideRowModelModule,
    ColDef,
    ColumnApiModule,
    CustomFilterModule,
    DragStoppedEvent,
    EventApiModule,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ModuleRegistry,
    RowClassRules,
    RowSelectionModule,
    RowSelectionOptions,
    RowStyleModule,
    SelectionChangedEvent,
    SelectionColumnDef,
    SortChangedEvent,
    SortDirection,
    TextFilterModule,
    themeQuartz
} from 'ag-grid-community';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FlightLogWithDuration } from '../flight-log-list-dialogue.component';
import { FlightLogFloatingFilter } from './flight-log-floating-filter.component';
import { TemplateCellRenderer } from './template-cell-renderer.component';

// AG Grid 33+ is modular: register only what this table uses. Column moving,
// resizing and the header sort UI are part of the core and need no module.
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    RowSelectionModule,
    ColumnApiModule,
    EventApiModule,
    RowStyleModule,
    CellStyleModule,
    CustomFilterModule,
    TextFilterModule
]);

const ROW_HEIGHT = 50;
const HEADER_HEIGHT = 40;
const FLOATING_FILTERS_HEIGHT = 34;
const MIN_LIMIT = 5;
const SEARCH_DEBOUNCE = 700;
const RESIZE_DEBOUNCE = 350;

/**
 * A row pre-rendered for display: `data` holds the original row, every other
 * key holds the formatted string for that column. Mirrors static-table's
 * approach so plain cells are property reads, not per-change-detection
 * formatter calls.
 */
export interface RenderedRow {
    data: FlightLogWithDuration;
    [key: string]: any;
}

/**
 * Experimental replacement for `static-table`, backed by AG Grid Community.
 * The API mirrors a subset of static-table's inputs/outputs, and the cell
 * templates are reused via the same `columnTemplate`/`rowActions` directives,
 * so the surrounding dialogue markup stays unchanged.
 *
 * Paging, sorting and filtering are all external (server side): the grid only
 * ever holds the current page. The page size is derived from the available
 * viewport height (`pageSizeChanged`) so the table exactly fills its container.
 */
@Component({
    selector: 'flight-log-datatable',
    templateUrl: './flight-log-datatable.component.html',
    styleUrl: './flight-log-datatable.component.scss'
})
export class FlightLogDatatableComponent
    implements OnInit, OnChanges, AfterContentInit, AfterViewInit, OnDestroy
{
    @Input() availableColumns: TableColumn[];
    @Input() tableData: FlightLogWithDuration[];
    @Input() tableConfig: TableConfig;
    @Input() columnSorting?: ColumnSortPreferences;
    @Input() tableSearch?: TableSearchValues;
    @Input() selectedColumns?: string[];
    @Input() columnOrder?: string[];
    @Input() templateActionsWidth = 0;

    @Output() pageChanged = new EventEmitter<number>();
    @Output() sortOrderChanged = new EventEmitter<ColumnSortPreferences>();
    @Output() search = new EventEmitter<TableSearchValues>();
    @Output() selectedColumnsChanged = new EventEmitter<string[]>();
    @Output() columnOrderChanged = new EventEmitter<string[]>();
    @Output() pageSizeChanged = new EventEmitter<number>();

    @ContentChildren(ColumnTemplateDirective)
    templates: QueryList<ColumnTemplateDirective>;
    @ContentChild(RowActionsDirective) rowActionsTemplate: RowActionsDirective;

    @ViewChild('scrollRegion', { static: true })
    scrollRegion: ElementRef<HTMLDivElement>;

    readonly theme = themeQuartz.withParams({
        headerHeight: HEADER_HEIGHT,
        rowHeight: ROW_HEIGHT,
        fontSize: 13,
        spacing: 6,
        borderRadius: 2
    });
    readonly headerHeight = HEADER_HEIGHT;
    readonly floatingFiltersHeight = FLOATING_FILTERS_HEIGHT;
    readonly rowHeight = ROW_HEIGHT;
    readonly sortingOrder: SortDirection[] = ['asc', 'desc'];
    readonly defaultColDef: ColDef = {
        flex: 1,
        minWidth: 110,
        resizable: true,
        sortable: false,
        suppressHeaderMenuButton: true
    };
    readonly rowSelection: RowSelectionOptions = {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: false
    };
    readonly selectionColumnDef: SelectionColumnDef = {
        width: 40,
        maxWidth: 40,
        flex: 0,
        pinned: 'left',
        lockPosition: 'left',
        suppressMovable: true,
        resizable: false
    };
    readonly rowClassRules: RowClassRules<RenderedRow> = {
        'row-warning': p =>
            this.tableConfig?.isWarningFunction?.(p.data.data) ?? false
    };
    getRowId = (p: GetRowIdParams<RenderedRow>) => String(p.data.data.id);

    // Selection filter values can be objects (e.g. collectedBy's {id, type})
    // whose references change whenever searchOptions are rebuilt
    compareSearchOption = (a: any, b: any) =>
        JSON.stringify(a) === JSON.stringify(b);

    rows: RenderedRow[] = [];
    columnDefs: ColDef[] = [];
    displayColumns: TableColumn[] = [];
    selectableColumns: TableColumn[] = [];
    selected: RenderedRow[] = [];
    filterForm = new FormGroup({});

    private api?: GridApi;
    private allColumns: TableColumn[] = [];
    private visibleKeys: string[] = [];
    private templateLookup: { [key: string]: TemplateRef<any> } = {};
    private regionHeight$ = new Subject<number>();
    private resizeObserver?: ResizeObserver;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(private ngZone: NgZone) {}

    get selectedRows(): FlightLogWithDuration[] {
        return this.selected.map(r => r.data);
    }

    get actionsWidth(): number {
        // 36px per icon button plus the reduced cell padding (see .actions-cell)
        return Math.max(this.templateActionsWidth, 1) * 36 + 16;
    }

    ngOnInit() {
        this.filterForm.valueChanges
            .pipe(
                debounceTime(SEARCH_DEBOUNCE),
                distinctUntilChanged(
                    (a, b) => JSON.stringify(a) === JSON.stringify(b)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(values =>
                this.search.emit(this.normaliseSearchValues(values))
            );
    }

    ngOnChanges(changes: SimpleChanges) {
        const columnsChanged =
            changes.availableColumns ||
            changes.selectedColumns ||
            changes.columnOrder;
        if (columnsChanged) {
            this.rebuildColumns();
        } else if (changes.columnSorting || changes.templateActionsWidth) {
            this.buildColumnDefs();
        }
        if (changes.tableData || columnsChanged) {
            this.renderRows();
        }
        if (changes.tableData) {
            // New results mean the page, filters, or tab changed, so any
            // previous selection may reference rows that are gone.
            this.selected = [];
            this.api?.deselectAll();
        }
        if (changes.tableSearch && this.tableSearch != null) {
            this.filterForm.patchValue(this.tableSearch, { emitEvent: false });
        }
    }

    ngAfterContentInit() {
        const buildLookup = () => {
            this.templateLookup = this.templates.reduce(
                (acc, t) => ({ ...acc, [t.column]: t.tpl }),
                {}
            );
            // Column defs carry the template refs, so rebuild once known
            this.buildColumnDefs();
        };
        buildLookup();
        this.templates.changes
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(buildLookup);
    }

    ngAfterViewInit() {
        this.regionHeight$
            .pipe(debounceTime(RESIZE_DEBOUNCE), takeUntil(this.ngUnsubscribe$))
            .subscribe(height => {
                const limit = Math.max(
                    MIN_LIMIT,
                    Math.floor(
                        (height - HEADER_HEIGHT - FLOATING_FILTERS_HEIGHT) /
                            ROW_HEIGHT
                    )
                );
                if (limit !== this.tableConfig.limit) {
                    // ResizeObserver callbacks run outside the Angular zone
                    this.ngZone.run(() => this.pageSizeChanged.emit(limit));
                }
            });
        this.resizeObserver = new ResizeObserver(([entry]) =>
            this.regionHeight$.next(entry.contentRect.height)
        );
        this.resizeObserver.observe(this.scrollRegion.nativeElement);
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    isColumnVisible(key: string): boolean {
        return this.visibleKeys.includes(key);
    }

    toggleColumn(key: string) {
        const selectedColumns = this.visibleKeys.includes(key)
            ? this.visibleKeys.filter(k => k !== key)
            : [...this.visibleKeys, key];
        this.selectedColumnsChanged.emit(selectedColumns);
    }

    onFooterPageChanged(page: number) {
        // ngx-bootstrap's pagination is 1-based and re-emits on model updates
        if (page - 1 !== (this.tableConfig.currentPage ?? 0)) {
            this.pageChanged.emit(page - 1);
        }
    }

    onGridReady(event: GridReadyEvent) {
        this.api = event.api;
    }

    onSortChanged(event: SortChangedEvent) {
        const sorted = event.api.getColumnState().find(s => s.sort != null);
        if (sorted == null) {
            return;
        }
        const sorting = {
            column: sorted.colId,
            ascending: sorted.sort === 'asc'
        };
        if (
            sorting.column === this.columnSorting?.column &&
            sorting.ascending === this.columnSorting?.ascending
        ) {
            return;
        }
        this.sortOrderChanged.emit(sorting);
    }

    onDragStopped(event: DragStoppedEvent) {
        const order = event.api
            .getColumnState()
            .map(s => s.colId)
            .filter(id => this.displayColumns.some(c => c.key === id));
        const current = this.displayColumns.map(c => c.key);
        if (order.join() === current.join()) {
            return;
        }
        this.columnOrderChanged.emit(order);
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        this.selected = event.api.getSelectedRows();
    }

    private rebuildColumns() {
        this.allColumns = (this.availableColumns ?? []).map(initColumn);
        this.selectableColumns = this.allColumns.filter(c => c.selectable);
        this.visibleKeys =
            this.selectedColumns ?? findDefaultColumnSelection(this.allColumns);
        const orderIx = (key: string) => {
            const ix = this.columnOrder?.indexOf(key) ?? -1;
            return ix === -1 ? Number.MAX_SAFE_INTEGER : ix;
        };
        // Stable sort: ordered keys first, the rest keep definition order
        this.displayColumns = this.allColumns
            .filter(c => this.visibleKeys.includes(c.key))
            .map((c, i) => ({ c, i }))
            .sort((a, b) => orderIx(a.c.key) - orderIx(b.c.key) || a.i - b.i)
            .map(x => x.c);
        this.syncFilterControls();
        this.buildColumnDefs();
    }

    /**
     * Column defs follow displayColumns order, so re-emitting them after a
     * reorder or visibility change is the whole synchronisation.
     */
    private buildColumnDefs() {
        const dataColumns: ColDef[] = this.displayColumns.map(col => {
            const template = this.templateLookup[col.key];
            const searchable = col.searchable !== false;
            return {
                colId: col.key,
                field: col.key,
                headerName: col.name,
                sortable: col.sortable !== false,
                // Sorting is external: never let the grid re-sort the page
                comparator: () => 0,
                sort:
                    this.columnSorting?.column === col.key
                        ? this.columnSorting.ascending
                            ? 'asc'
                            : 'desc'
                        : null,
                cellRenderer:
                    template != null ? TemplateCellRenderer : undefined,
                cellRendererParams: { template, tableColumn: col },
                floatingFilter: searchable,
                // A column filter must exist for the floating filter row to
                // render; its model is never used
                filter: searchable ? 'agTextColumnFilter' : false,
                suppressFloatingFilterButton: true,
                floatingFilterComponent: FlightLogFloatingFilter,
                floatingFilterComponentParams: { tableColumn: col }
            };
        });
        const actionsColumn: ColDef[] =
            this.rowActionsTemplate != null
                ? [
                      {
                          colId: '__actions',
                          headerName: '',
                          width: this.actionsWidth,
                          flex: 0,
                          sortable: false,
                          resizable: false,
                          suppressMovable: true,
                          pinned: 'right',
                          lockPosition: 'right',
                          cellClass: 'actions-cell',
                          cellRenderer: TemplateCellRenderer,
                          cellRendererParams: {
                              template: this.rowActionsTemplate.tpl
                          }
                      }
                  ]
                : [];
        this.columnDefs = [...dataColumns, ...actionsColumn];
    }

    private renderRows() {
        const defaultFormatter = (val: any) =>
            val != null ? val.toString() : '';
        this.rows = (this.tableData ?? []).map(row =>
            this.allColumns.reduce(
                (acc, col) => ({
                    ...acc,
                    [col.key]:
                        (col.formatterFunction ?? defaultFormatter)(
                            resolveValue(row, col.value),
                            row
                        ) ?? ''
                }),
                { data: row } as RenderedRow
            )
        );
    }

    private syncFilterControls() {
        const wanted = this.displayColumns
            .filter(c => c.searchable !== false)
            .map(c => c.key);
        for (const key of Object.keys(this.filterForm.controls)) {
            if (!wanted.includes(key)) {
                this.filterForm.removeControl(key, { emitEvent: false });
            }
        }
        for (const key of wanted) {
            if (this.filterForm.get(key) == null) {
                this.filterForm.addControl(key, new FormControl(null), {
                    emitEvent: false
                });
            }
        }
    }

    /**
     * Widen daterange values to whole days, matching static-table's
     * behaviour, so that logs on the end date are included.
     */
    private normaliseSearchValues(values: {
        [key: string]: any;
    }): TableSearchValues {
        return Object.keys(values).reduce((acc, key) => {
            const col = this.allColumns.find(c => c.key === key);
            const value = values[key];
            if (
                col?.searchable === 'daterange' &&
                Array.isArray(value) &&
                value[0] != null &&
                value[1] != null
            ) {
                return {
                    ...acc,
                    [key]: [
                        moment(value[0]).startOf('day').toDate(),
                        moment(value[1]).endOf('day').toDate()
                    ]
                };
            }
            return { ...acc, [key]: value };
        }, {});
    }
}
